/* AIVIQ product top section (sections/aiviq-pdp-main.liquid): gallery, viewer, add to cart through the
   AIVIQ cart drawer (it listens for ajaxProduct:added / ajaxProduct:error), the phone sticky bar and the
   in-page links. No pollers, no scroll listeners; idempotent on shopify:section:load. */
(() => {
  const root = document.documentElement;
  const smooth = () => window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
  const cartAddUrl = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart/add.js';

  // Header height, for the sticky columns and the anchor offset
  const header = () => document.querySelector('.header-section') || document.querySelector('#SiteHeader');
  const setHeader = () => {
    const h = header();
    if (h) root.style.setProperty('--ah-header-h', Math.round(h.getBoundingClientRect().height) + 'px');
  };
  const headerOffset = () => {
    const h = header();
    if (!h) return 0;
    const pos = getComputedStyle(h).position;
    const r = h.getBoundingClientRect();
    return pos === 'fixed' || pos === 'sticky' || r.top >= 0 ? Math.max(0, r.bottom) : 0;
  };

  const go = (target) => {
    const det = target.closest('details');
    if (det && !det.open) det.open = true;
    const y = target.getBoundingClientRect().top + window.scrollY - headerOffset() - 16;
    window.scrollTo({ top: y, behavior: smooth() ? 'smooth' : 'auto' });
  };

  function init(sec) {
    if (!sec || sec.dataset.apReady) return;
    sec.dataset.apReady = '1';
    const ac = new AbortController();
    sec._apAbort = () => ac.abort();
    const opt = { signal: ac.signal };

    setHeader();
    const media = sec.querySelector('[data-ap-media]');
    const buy = sec.querySelector('[data-ap-buy]');
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => {
        if (media) {
          sec.style.setProperty('--ap-media-h', media.offsetHeight + 'px');
          sec.style.setProperty('--ap-media-w', media.offsetWidth + 'px');
        }
        if (buy) sec.style.setProperty('--ap-buy-h', buy.offsetHeight + 'px');
        setHeader();
      });
      if (media) ro.observe(media);
      if (buy) ro.observe(buy);
      const h = header();
      if (h) ro.observe(h);
      ac.signal.addEventListener('abort', () => ro.disconnect());
    }

    /* The type line under the name stays on one line (owner, 3 Oct 2026): the size steps down to fit, never under 12.5 px */
    const ty = sec.querySelector('.ap-ty');
    if (ty) {
      const fitTy = () => {
        ty.style.fontSize = '';
        ty.classList.remove('is-wrap');
        const room = ty.parentElement.clientWidth;
        const need = ty.scrollWidth;
        if (!room || need <= room) return;
        const base = parseFloat(getComputedStyle(ty).fontSize);
        const size = Math.floor((base * room / need) * 10) / 10;
        if (size >= 12.5) ty.style.fontSize = size + 'px'; else ty.classList.add('is-wrap');
      };
      fitTy();
      window.addEventListener('resize', fitTy, opt);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitTy);
    }

    /* Gallery */
    const track = sec.querySelector('[data-ap-track]');
    const slides = track ? [...track.children] : [];
    const thumbs = [...sec.querySelectorAll('[data-ap-thumb]')];
    const cur = sec.querySelector('[data-ap-cur]');
    const live = sec.querySelector('[data-ap-live]');
    let index = 0;
    let liveTimer;
    const mosaicOn = () => sec.hasAttribute('data-mosaic') && window.matchMedia('(min-width: 1280px)').matches;
    const setIndex = (i) => {
      if (i === index) return;
      index = i;
      if (cur) cur.textContent = i + 1;
      thumbs.forEach((t, k) => {
        t.classList.toggle('is-on', k === i);
        if (k === i) {
          t.setAttribute('aria-current', 'true');
          const rail = t.parentElement;
          if (rail.scrollHeight > rail.clientHeight) {
            const top = t.offsetTop - rail.offsetTop;
            if (top < rail.scrollTop || top + t.offsetHeight > rail.scrollTop + rail.clientHeight) rail.scrollTop = top - 8;
          }
        } else t.removeAttribute('aria-current');
      });
      slides.forEach((s, k) => s.querySelector('.ap-tile')?.setAttribute('tabindex', k === i ? '0' : '-1'));
      clearTimeout(liveTimer);
      liveTimer = setTimeout(() => { if (live) live.textContent = `Bild ${i + 1} av ${slides.length}`; }, 400);
    };
    const show = (i) => {
      if (!track || mosaicOn()) return;
      const k = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollLeft = slides[k].offsetLeft - track.offsetLeft - (parseFloat(getComputedStyle(track).paddingLeft) || 0);
      setIndex(k);
    };
    if (track && slides.length > 1) {
      /* a few measurements per scroll event: run directly, so the counter follows the finger */
      track.addEventListener('scroll', () => {
        if (mosaicOn()) return;
          // nearest slide to the track's start (slides can be narrower than the track on tablets)
          const pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
          let best = 0, dist = Infinity;
          slides.forEach((s, k) => { const d = Math.abs(s.offsetLeft - track.offsetLeft - pad - track.scrollLeft); if (d < dist) { dist = d; best = k; } });
        setIndex(best);
      }, { passive: true, signal: ac.signal });
      /* phones and tablets: the photos next in line are fetched ahead, so a swipe never lands on an empty slide */
      const ahead = (i) => { for (let k = i + 1; k <= i + 2 && k < slides.length; k++) { const im = slides[k].querySelector('img[loading="lazy"]'); if (im) im.loading = 'eager'; } };
      ahead(0);
      track.addEventListener('scroll', () => ahead(index), { passive: true, signal: ac.signal });
      sec.querySelectorAll('[data-ap-step]').forEach((b) => b.addEventListener('click', () => show(index + Number(b.dataset.apStep)), opt));
      thumbs.forEach((t, k) => t.addEventListener('click', () => show(k), opt));
      track.addEventListener('keydown', (e) => {
        if (mosaicOn()) return;
        const map = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: slides.length - 1 };
        if (e.key in map) { e.preventDefault(); show(map[e.key]); }
      }, opt);
    }

    /* Viewer */
    const view = sec.querySelector('[data-ap-view]');
    const fig = sec.querySelector('[data-ap-view-fig]');
    const vcnt = sec.querySelector('[data-ap-view-cnt]');
    const tiles = slides.map((s) => s.querySelector('.ap-tile'));
    let vIndex = 0;
    let lastFocus = null;
    const paint = (i) => {
      vIndex = (i + tiles.length) % tiles.length;
      const t = tiles[vIndex];
      if (!t || !fig) return;
      fig.classList.remove('is-zoom');
      fig.textContent = '';
      if (t.dataset.video) {
        const v = document.createElement('video');
        v.src = t.dataset.video;
        v.controls = true;
        v.playsInline = true;
        v.autoplay = true;
        fig.append(v);
      } else {
        const img = document.createElement('img');
        img.src = t.dataset.full;
        img.alt = t.querySelector('img')?.alt || '';
        img.addEventListener('click', () => fig.classList.toggle('is-zoom'));
        fig.append(img);
      }
      if (vcnt) vcnt.textContent = `${vIndex + 1} / ${tiles.length}`;
    };
    // The theme's PhotoSwipe (the .pswp template sits in theme.liquid): opens from the tile, swipe, pinch and
    // double-tap zoom, swipe down to close. The dialog below is only the fallback.
    const pswp = async (i) => {
      const el = document.querySelector('.pswp');
      if (!el) return false;
      try {
        await import('vendor.photoswipe');
        await import('vendor.photoswipe-ui-default');
      } catch (x) { return false; }
      if (!window.PhotoSwipe || !window.PhotoSwipeUI_Default) return false;
      const items = tiles.map((t) => {
        const img = t.querySelector('img');
        if (t.dataset.video) return { html: `<div class="ap-pswp-vid"><video src="${t.dataset.video}" controls playsinline preload="metadata"></video></div>`, el: img };
        return { src: t.dataset.full, w: Number(t.dataset.w) || 1600, h: Number(t.dataset.h) || 1600, msrc: img ? img.currentSrc || img.src : '', el: img };
      });
      const cnt = document.createElement('div');
      cnt.className = 'ap-pswp__cnt';
      el.querySelector('.pswp__ui')?.append(cnt);
      document.body.classList.add('photoswipe-open');
      lastFocus = document.activeElement;
      const g = new window.PhotoSwipe(el, window.PhotoSwipeUI_Default, items, {
        index: i, history: false, shareEl: false, fullscreenEl: false, captionEl: false, counterEl: false, zoomEl: false,
        bgOpacity: 1, closeOnScroll: false, tapToToggleControls: false, clickToCloseNonZoomable: false, preloaderEl: false,
        showHideOpacity: !smooth(), showAnimationDuration: smooth() ? 300 : 0, hideAnimationDuration: smooth() ? 300 : 0,
        getThumbBoundsFn: (k) => {
          const img = items[k].el;
          if (!img || items[k].html) return undefined;
          const r = img.getBoundingClientRect();
          const ar = (img.naturalWidth && img.naturalHeight) ? img.naturalWidth / img.naturalHeight : 1;
          let w = r.width, h = r.height, x = r.left, y = r.top;
          if (getComputedStyle(img).objectFit === 'contain') {
            if (ar > r.width / r.height) { h = r.width / ar; y += (r.height - h) / 2; } else { w = r.height * ar; x += (r.width - w) / 2; }
          }
          return { x, y: y + window.scrollY, w };
        },
      });
      const upd = () => {
        cnt.textContent = `${g.getCurrentIndex() + 1} / ${items.length}`;
        el.querySelectorAll('video').forEach((v) => v.pause());
        show(g.getCurrentIndex());
      };
      g.listen('afterChange', upd);
      g.listen('destroy', () => {
        el.querySelectorAll('video').forEach((v) => v.pause());
        root.classList.remove('ap-pswp-open');
        cnt.remove();
        document.body.classList.remove('photoswipe-open');
        if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
      });
      // the marker sits on <html>: PhotoSwipe rewrites the .pswp class list while it opens and closes
      root.classList.add('ap-pswp-open');
      g.init();
      upd();
      // opened on the film tile: the tap was the wish to see the film, so it starts (with sound; muted if the browser refuses)
      if (items[i] && items[i].html) {
        const v = g.currItem && g.currItem.container ? g.currItem.container.querySelector('video') : null;
        if (v) { const p = v.play(); if (p && p.catch) p.catch(() => { v.muted = true; v.play().catch(() => {}); }); }
      }
      return true;
    };
    const open = async (i) => {
      if (await pswp(i)) return;
      if (!view || typeof view.showModal !== 'function') {
        const t = tiles[i];
        if (t) window.open(t.dataset.video || t.dataset.full, '_blank', 'noopener');
        return;
      }
      lastFocus = document.activeElement;
      paint(i);
      view.showModal();
      root.classList.add('ap-viewing');
    };
    sec.querySelectorAll('[data-ap-open]').forEach((b) => b.addEventListener('click', () => open(Number(b.dataset.apOpen)), opt));
    if (view) {
      sec.querySelectorAll('[data-ap-vstep]').forEach((b) => b.addEventListener('click', () => paint(vIndex + Number(b.dataset.apVstep)), opt));
      sec.querySelector('[data-ap-close]')?.addEventListener('click', () => view.close(), opt);
      view.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') paint(vIndex - 1);
        if (e.key === 'ArrowRight') paint(vIndex + 1);
        if (e.key === 'Escape') { e.preventDefault(); view.close(); }
      }, opt);
      // Clean up when the dialog closes by any route. A theme script swallows the dialog's own
      // close event on this page, so watch the open attribute instead.
      const mo = new MutationObserver(() => {
        if (view.open) return;
        fig.textContent = '';
        root.classList.remove('ap-viewing');
        if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
      });
      mo.observe(view, { attributes: true, attributeFilter: ['open'] });
      ac.signal.addEventListener('abort', () => mo.disconnect());
    }

    /* MultiBrew capsule picker (data-ap-cp): the ticked adapters go into the cart with the machine; the summary shows the
       package with the live prices, and the shop's automatic discount (data-pct from data-min adapters) */
    const cp = sec.querySelector('[data-ap-cp]');
    const cpItems = () => {
      const map = new Map();
      if (cp) cp.querySelectorAll('input[data-ap-cp-opt]:checked').forEach((i) => {
        const ids = i.dataset.ids.split(','), prices = i.dataset.prices.split(',');
        ids.forEach((id, k) => map.set(id, Number(prices[k])));
      });
      return map;
    };
    if (cp) {
      const pct = Number(cp.dataset.pct), min = Number(cp.dataset.min), base = Number(cp.dataset.base);
      const kr = (ore) => {
        const s = (ore / 100).toFixed(2).split('.');
        return s[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0') + (s[1] === '00' ? '' : ',' + s[1]) + ' kr';
      };
      const mains = [...cp.querySelectorAll('input[data-ap-cp-opt][data-main]')];
      const all = cp.querySelector('[data-ap-cp-all]');
      const upd = () => {
        const m = cpItems();
        let sum = 0; m.forEach((p) => { sum += p; });
        const off = m.size >= min ? Math.round(sum * pct / 100) : 0;
        /* the E.S.E. filter is a filter, not an adapter: »+ 4 adaptere + E.S.E.-filter« */
        const filters = cp.querySelectorAll('input[data-ap-cp-opt]:not([data-main]):checked').length;
        const picked = m.size - filters;
        cp.querySelectorAll('input[data-ap-cp-opt]').forEach((i) => i.closest('li').classList.toggle('is-on', i.checked));
        cp.querySelector('[data-ap-cp-pk]').textContent = cp.dataset.name + (picked ? ' + ' + picked + (picked === 1 ? ' adapter' : ' adaptrar') : '') + (filters ? ' + E.S.E.-filter' : '') + (off ? ' · paketpris, spara ' + kr(off) : '');
        cp.querySelector('[data-ap-cp-total]').textContent = kr(base + sum - off);
        cp.querySelector('[data-ap-cp-total]').classList.toggle('is-sale', !!off);
        /* with the package price: the normal total struck through next to the new one (owner, 4 Oct 2026) */
        const was = cp.querySelector('[data-ap-cp-was]');
        if (was) { was.querySelector('s').textContent = kr(base + sum); was.hidden = !off; }
        if (all) all.textContent = mains.every((i) => i.checked) ? 'Välj bort' : 'Välj alla fyra';
      };
      cp.addEventListener('change', upd, opt);
      if (all) all.addEventListener('click', () => { const on = !mains.every((i) => i.checked); mains.forEach((i) => { i.checked = on; }); upd(); }, opt);
      upd();
    }

    /* Add to cart: the drawer's capture listener sees the prevented submit and shows its pending state */
    const form = sec.querySelector('[data-ap-form]');
    const err = sec.querySelector('[data-ap-err]');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = e.submitter && e.submitter.matches('[data-ap-add]') ? e.submitter : form.querySelector('[data-ap-add]');
        if (!btn || btn.getAttribute('aria-busy') === 'true') return;
        btn.setAttribute('aria-busy', 'true');
        if (err) err.textContent = '';
        try {
          const extra = cpItems();
          const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
          let body = new FormData(form);
          if (extra.size) {
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify({ items: [{ id: Number(form.querySelector('[name="id"]').value), quantity: 1 }].concat([...extra.keys()].map((id) => ({ id: Number(id), quantity: 1 }))) });
          }
          const res = await fetch(cartAddUrl, { method: 'POST', headers, body });
          const data = await res.json();
          if (!res.ok) throw data;
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail: { product: data, addToCartBtn: btn } }));
        } catch (x) {
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', { bubbles: true, detail: { errorMessage: x && x.description } }));
          if (err) err.textContent = (x && x.description) || 'Varan kunde inte läggas i varukorgen. Försök igen.';
        } finally {
          btn.removeAttribute('aria-busy');
        }
      }, opt);
    }

    /* Phone sticky bar: shows once the main button has passed under the header */
    const bar = sec.querySelector('[data-ap-bar]');
    const cta = sec.querySelector('.ap-form [data-ap-add]');
    if (bar && cta && 'IntersectionObserver' in window) {
      let ctaGone = false;
      let footerIn = false;
      const update = () => {
        const on = ctaGone && !footerIn && window.matchMedia('(max-width: 767px)').matches;
        bar.classList.toggle('is-on', on);
        bar.toggleAttribute('inert', !on);
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        root.style.setProperty('--ap-bar-h', on ? bar.offsetHeight + 'px' : '0px');
      };
      const io = new IntersectionObserver(([en]) => {
        ctaGone = !en.isIntersecting && en.boundingClientRect.top < (en.rootBounds ? en.rootBounds.top : 0) + 1;
        update();
      }, { rootMargin: `-${Math.round(headerOffset())}px 0px 0px 0px` });
      io.observe(cta);
      const foot = document.querySelector('.shopify-section-group-footer-group');
      const io2 = new IntersectionObserver(([en]) => { footerIn = en.isIntersecting; update(); });
      if (foot) io2.observe(foot);
      sec.querySelector('[data-ap-top]')?.addEventListener('click', () => {
        window.scrollTo({ top: Math.max(0, sec.getBoundingClientRect().top + window.scrollY - headerOffset()), behavior: smooth() ? 'smooth' : 'auto' });
        cta.focus({ preventScroll: true });
      }, opt);
      ac.signal.addEventListener('abort', () => { io.disconnect(); io2.disconnect(); });
    }

    /* In-page links: hide links whose target is not on the page (yet); open <details> on the way */
    const reviews = document.querySelector('#anmeldelser') || [...document.querySelectorAll('.shopify-section')].find((s) => /rivyo|wc_review/i.test(s.innerHTML.slice(0, 4000)) && !s.contains(sec));
    if (reviews && !reviews.id.includes('anmeldelser')) {
      const anchor = document.createElement('span');
      anchor.id = 'anmeldelser';
      reviews.prepend(anchor);
    }
    sec.querySelectorAll('a[href^="#"]').forEach((a) => {
      const id = decodeURIComponent(a.getAttribute('href').slice(1));
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) {
        const p = a.closest('p');
        if (p && p.textContent.trim() === a.textContent.trim()) p.hidden = true;
        else a.hidden = true;
        return;
      }
      a.addEventListener('click', (e) => { e.preventDefault(); go(target); history.replaceState(null, '', '#' + id); }, opt);
    });
    sec.querySelectorAll('.ap-sw__lk').forEach((w) => { if (![...w.children].some((a) => !a.hidden)) w.hidden = true; });
  }

  const boot = () => document.querySelectorAll('[data-ap]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (e) => init(e.target.querySelector('[data-ap]')));
  document.addEventListener('shopify:section:unload', (e) => {
    const s = e.target.querySelector('[data-ap]');
    if (s && s._apAbort) { s._apAbort(); delete s.dataset.apReady; }
  });
})();
